<template>
  <BaseDropdown
    :is-open="isOpen"
    id="notification-dropdown"
    @toggle="$emit('toggle', $event)"
  >
    <template #trigger>
      <div class="notification-dropdown__trigger">
        <NotificationBell :count="count" />
      </div>
    </template>

    <template #content>
      <div class="notification-dropdown__menu">
        <h2 class="notification-dropdown__menu-title">{{ $t('notifications.title') }}</h2>
        <div
          v-if="notifications.length"
          ref="listRef"
          class="notification-dropdown__list"
        >
          <div
            v-for="(item, index) in notifications"
            :key="index"
            class="notification-dropdown__item"
            :class="[item.level]"
            role="menuitem"
          >
            <slot :item="item" />
          </div>
        </div>

        <div v-else class="notification-dropdown__empty">
          <slot name="empty">
            {{ $t('notifications.empty') }}
          </slot>
        </div>

        <div v-if="isLoading" class="notification-dropdown__loader">
          <slot name="loader">
            <div class="notification-dropdown__spinner" />
          </slot>
        </div>
      </div>
    </template>
  </BaseDropdown>
</template>

<script setup>
import { ref } from 'vue';
import { useInfiniteScroll } from '@vueuse/core';
import BaseDropdown from '@/components/ui/BaseDropdown.vue';
import NotificationBell from '@/components/Icons/NotificationBell.vue';

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  },
  count: {
    type: Number,
    default: 0
  },
  notifications: {
    type: Array,
    default: () => []
  },
  isLoading: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['toggle', 'load-more']);

const listRef = ref(null);

const {isLoading: scrollLoading} = useInfiniteScroll(
  listRef,
  () => {
    if (!props.isLoading) {
      emit('load-more');
    }
  },
  {
    distance: 0,
    interval: 200,
    canLoadMore: () => !props.isLoading && !scrollLoading.value
  }
);
</script>

<style lang="scss" scoped>
@use "@/styles/variables" as *;

.notification-dropdown {
  &__trigger {
    cursor: pointer;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      color: var(--general-color);
    }
  }

  &__menu {
    width: 600px;
    min-height: 410px;
    max-height: 600px;
    height: 100%;
    padding: 22px 28px;
    background-color: $white-color;
    border-radius: 11px;
    overflow: hidden;

    &-title {
      color: #404040;
      text-align: center;
      font-size: 20px;
      font-style: normal;
      font-weight: 400;
      line-height: 30px;
      text-transform: uppercase;
      margin-bottom: 20px;
    }

    @media screen and (max-width: 640px) {
      width: auto;
    }
  }

  &__list {
    max-height: 310px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 2px;
    background-color: #F4F3FC;
  }

  &__item {
    padding: 10px 20px;
    border-left: 4px solid;
    transition: background-color 0.2s ease;
    background-color: $white-color;

    &.info {
      border-left-color: #70C4DF;
    }
    &.success {
      border-left-color: #70DF7B;
    }
    &.warning {
      border-left-color: #FFC107;
    }
    &.error {
      border-left-color: #DF7070;
    }

    &:hover {
        background-color: $ghost-white-color;
    }
  }

  &__empty {
    padding: 24px 16px;
    text-align: center;
    color: rgba(0, 0, 0, 0.5);
  }

  &__loader {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(4px);
    z-index: 1;
  }

  // the global .spinner
  &__spinner {
    width: 24px;
    height: 24px;
    border: 3.4px solid var(--general-opacity-color);
    border-top-color: var(--general-color);
    border-radius: 50%;
    animation: spin 1s cubic-bezier(0.55, 0.15, 0.45, 0.85) infinite;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
